
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from './services/firebase';
import { preloadAllCards } from './components/CardUI';
import { SUIT_IMG_MAP } from './lib/constants';
import GameBoard from './components/GameBoard';
import MainMenu from './components/MainMenu';
import ConfigScreen from './components/ConfigScreen';
import BackgroundParticles from './components/BackgroundParticles';

import { Card } from './types';
import { useAuth } from './hooks/useAuth';
import { useUserProfile } from './hooks/useUserProfile';
import { useCoins } from './hooks/useCoins';
import { useAchievements } from './hooks/useAchievements';
import { useTournament, type Tournament } from './hooks/useTournament';
import MenuOnline, { WaitingRoom } from './components/MenuOnline';
import LevelUpModal from './components/LevelUpModal';
import Shop from './components/Shop';
import AuthModal from './components/AuthModal';
import AchievementsModal from './components/AchievementsModal';
import { AnimatePresence } from 'motion/react';
import OnlineGameView from './components/OnlineGameView';
import { listenMatch, leaveMatch, markDisconnected, clearPendingMatch, challengeFriend, listenChallenges, removeChallenge, joinMatch, type MatchData, type ChallengeData } from './services/matchmaking';
import { calculateRoundScores, getHandSum } from './lib/gameUtils';
import { getBestMove } from './lib/aiLogic';
import { GAME_CONSTANTS, PREMIUM_ENABLED, COIN_REWARDS } from './lib/constants';
import { useGameState } from './hooks/useGameState';
import CookieConsent from './components/CookieConsent';
import VideoAdModal from './components/VideoAdModal';
import FriendsModal from './components/FriendsModal';
import IncomingChallengeModal from './components/IncomingChallengeModal';
import DailyChallenges from './components/DailyChallenges';
import PrivateTournamentModal from './components/PrivateTournamentModal';
import Footer from './components/Footer';
import { syncBoostState, decrementBoost, isBoostActive } from './lib/rewardedVideo';
import { trackEvent } from './lib/analytics';
import { recordOperationalMetric } from './lib/operationalMetrics';



export default function App() {
  const gs = useGameState();
  const {
    gameState, setGameState,
    selectedCard, setSelectedCard,
    hoveredCardId, setHoveredCardId,
    possibleCaptures, setPossibleCaptures,
    showCapturesMenu, setShowCapturesMenu,
    capturingCards, setCapturingCards,
    revealedCardIds, setRevealedCardIds,
    revealingCardIds, setRevealingCardIds,
    dealId, setDealId,
    errorMessage, setErrorMessage,
    handleNextRound,
    handleLowHandDeclaration,
    handleThreeAcesDeclaration,
    performCapture,
    playCard,
    scopaParticleSeeds,
  } = gs;

  const [showRules, setShowRules] = useState(false);

  // === ONLINE MULTIPLAYER STATE ===
  const { 
    user, 
    loading: authLoading, 
    login,
    logout,
    loginWithGoogle,
    linkWithGoogle,
    registerAndLinkEmail,
    loginWithEmail
  } = useAuth();

  // User Profile & XP Hook
  const { profile, loading: profileLoading, updateDisplayName, updateProfileCustomization, recordMatchOutcome, updateOwnedSkins, equipSkin } = useUserProfile(user?.uid);
const { balance: coinsBalance, earnCoins, spendCoins } = useCoins(user);
  const { achievements, loading: achievementsLoading, checkAndSave } = useAchievements(user);

  // Refs to track if game over has been recorded to prevent multiple triggers
  const hasRecordedOfflineRef = useRef(false);
  const hasRecordedOnlineRef = useRef<string | null>(null);

  const [gameMode, setGameMode] = useState<'menu' | 'config' | 'ai' | 'online'>('menu');
  const [cpuDifficulty, setCpuDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [showConfig, setShowConfig] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 390);

  // Ref to latest gameState for AI effect (avoids over-firing on every state change)
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  const [onlineMatchId, setOnlineMatchId] = useState<string | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [onlineStatus, setOnlineStatus] = useState<'menu' | 'waiting' | 'playing'>('menu');
  const [showLevelUpLevel, setShowLevelUpLevel] = useState<number | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showShopModal, setShowShopModal] = useState(false);
  const [showAchievementsModal, setShowAchievementsModal] = useState(false);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [showDailyChallenges, setShowDailyChallenges] = useState(false);
  const [showTournament, setShowTournament] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState<ChallengeData | null>(null);

  // Auto-login on mount
  useEffect(() => {
    if (!user && !authLoading) {
      login();
    }
  }, [user, authLoading, login]);

  // Listen for incoming friend challenges on app load
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = listenChallenges(user.uid, (challenges) => {
      // Find the first challenge that has a matchCode (challenger already created the match)
      const ready = challenges.find((c) => c.matchCode);
      if (ready) {
        setPendingChallenge(ready);
      }
    });

    return unsubscribe;
  }, [user?.uid]);

  const [forceShowCookieConsent, setForceShowCookieConsent] = useState(false);
  const [showVideoAdModal, setShowVideoAdModal] = useState(false);
  // Dynamic Google AdSense tag injection based on GDPR consent
  useEffect(() => {
    const injectAdSenseScript = () => {
      const consent = localStorage.getItem('sbarazzo_cookie_consent');
      const adsConsent = localStorage.getItem('sbarazzo_cookie_ads');
      if (consent !== 'all' && !(consent === 'personalizza' && adsConsent === 'true')) return;

      const existingScript = document.getElementById('adsense-main-script');
      if (existingScript) return;

      const client = import.meta.env.VITE_ADSENSE_CLIENT_ID || 'ca-pub-3103514852994717';
      const script = document.createElement('script');
      script.id = 'adsense-main-script';
      script.async = true;
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
      script.setAttribute('crossorigin', 'anonymous');
      
      document.head.appendChild(script);
    };

    injectAdSenseScript();

    const handleConsentUpdate = () => {
      injectAdSenseScript();
    };

    window.addEventListener('cookie_consent_updated', handleConsentUpdate);
    return () => {
      window.removeEventListener('cookie_consent_updated', handleConsentUpdate);
    };
  }, []);

  // Boost sync on session start
  useEffect(() => {
    // Clear expired boost on session start
    syncBoostState();
    // Show startup modal after a short delay (only if consent already given)
    // TODO: riattivare VideoAdModal in fase successiva
    // const consent = localStorage.getItem('sbarazzo_cookie_consent');
    // if (consent === 'all') {
    //   const timer = setTimeout(() => setShowVideoAdModal(true), 2000);
    //   return () => clearTimeout(timer);
    // }
  }, []);

  // Graceful disconnect on tab close / page unload
  useEffect(() => {
    if (!onlineMatchId || !user?.uid) return;

    const handleUnload = () => {
      markDisconnected(onlineMatchId, user.uid);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        markDisconnected(onlineMatchId, user.uid);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onlineMatchId, user?.uid]);

  const [waitingMatch, setWaitingMatch] = useState<MatchData | null>(null);

  // Intercept browser-back / close when in an active online match
  useEffect(() => {
    if (gameMode !== 'online' || !onlineMatchId) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Sei sicuro di voler uscire dalla partita online?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gameMode, onlineMatchId]);

  const handleOnlineBack = useCallback(async () => {
    if (onlineMatchId && user) {
      // Non attendiamo leaveMatch in modo sincrono per evitare blocchi dell'interfaccia se l'utente è offline
      leaveMatch(onlineMatchId, user.uid).catch((err) => {
        console.error('[App] Error leaving match in background:', err);
      });
      clearPendingMatch();
    }
    decrementBoost();
    setOnlineStatus('menu');
    setOnlineMatchId(null);
    setGameMode('menu');
    setWaitingMatch(null);
  }, [onlineMatchId, user]);

  const handleMatchCreated = useCallback((code: string) => {
    setOnlineMatchId(code);
    setIsHost(true);
    setOnlineStatus('waiting');
  }, []);

  const handleMatchJoined = useCallback((code: string) => {
    setOnlineMatchId(code);
    setIsHost(false);
    setOnlineStatus('waiting');
  }, []);

  // Deep-linking join game on mount/URL change
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cleanPlayModeParam = () => {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('join');
      cleanUrl.searchParams.delete('mode');
      window.history.replaceState({}, document.title, `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    };
    const joinCode = urlParams.get('join');
    if (joinCode && /^[0-9]{6}$/.test(joinCode)) {
      // Clean URL parameters
      cleanPlayModeParam();
      // Join match
      setGameMode('online');
      handleMatchJoined(joinCode);
    } else {
      const modeParam = urlParams.get('mode');
      if (modeParam === 'offline') {
        cleanPlayModeParam();
        setGameMode('config');
      } else if (modeParam === 'online') {
        cleanPlayModeParam();
        setGameMode('online');
      } else if (modeParam === 'quick') {
        cleanPlayModeParam();
        hasRecordedOfflineRef.current = false;
        setCpuDifficulty('medium');
        gs.startGame(2);
        setGameMode('ai');
        setShowConfig(false);
        preloadAllCards();
        trackEvent('game_start', { mode: 'cpu', difficulty: 'medium', players: 2 });
        recordOperationalMetric('cpu_start_medium');
      }
    }
  }, [gs.startGame, handleMatchJoined]);

  // Sync match state and status transitions for all players
  useEffect(() => {
    if (!onlineMatchId) {
      setWaitingMatch(null);
      return;
    }

    const unsubscribe = listenMatch(onlineMatchId, (matchData) => {
      if (!matchData) {
        setOnlineStatus('menu');
        setOnlineMatchId(null);
        setWaitingMatch(null);
        clearPendingMatch();
        return;
      }
      setWaitingMatch(matchData);
      if (matchData.status === 'playing') {
        setOnlineStatus('playing');
      } else if (matchData.status === 'waiting') {
        setOnlineStatus('waiting');
      }
    }, (err) => {
      console.error('[App] listenMatch error:', err);
      setOnlineStatus('menu');
      setOnlineMatchId(null);
      setWaitingMatch(null);
      clearPendingMatch();
    });

    return () => unsubscribe();
  }, [onlineMatchId]);

  useEffect(() => {
    let ticking = false;
    const handleResize = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setViewportWidth(window.innerWidth);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update persistent stats/XP when game ends (CPU/offline)
  useEffect(() => {
    if (gameState?.phase === 'gameOver' && !hasRecordedOfflineRef.current) {
      hasRecordedOfflineRef.current = true;
      decrementBoost();
      const playerWon = gameState.players[0].totalScore > gameState.players[1].totalScore;
      const opponentWon = gameState.players[1].totalScore > gameState.players[0].totalScore;
      const tied = gameState.players[0].totalScore === gameState.players[1].totalScore;
      recordOperationalMetric(`cpu_complete_${tied ? 'draw' : playerWon ? 'win' : 'loss'}`);
      if (!tied) {
        recordMatchOutcome(
          playerWon,
          'cpu',
          'CPU',
          gameState.players[0].totalScore,
          gameState.players[1].totalScore
        ).then((res) => {
          if (res?.leveledUp) {
            setShowLevelUpLevel(res.newLevel);
          }
        });
        // Coin reward (PREMIUM_ENABLED)
        if (PREMIUM_ENABLED && user) {
          earnCoins(COIN_REWARDS.WIN_MATCH, 'match_win');
          // Streak bonus
          const currentStreak = profile?.streak || 0;
          if (currentStreak >= 5) {
            earnCoins(COIN_REWARDS.STREAK_BONUS_5, 'streak_5');
          } else if (currentStreak >= 3) {
            earnCoins(COIN_REWARDS.STREAK_BONUS_3, 'streak_3');
          }
        }
      } else if (!tied && PREMIUM_ENABLED && user) {
        // Loss: smaller coin reward
        earnCoins(5, 'match_loss');
      }
    }
  }, [gameState?.phase, recordMatchOutcome, user, profile, earnCoins]);

  // Update persistent stats/XP when game ends (Online multiplayer)
  useEffect(() => {
    if (waitingMatch && waitingMatch.status === 'finished' && onlineMatchId) {
      if (hasRecordedOnlineRef.current === onlineMatchId) return;
      hasRecordedOnlineRef.current = onlineMatchId;
      decrementBoost();
      clearPendingMatch();
      const myIdx = waitingMatch.players.findIndex(p => p.uid === user?.uid);
      if (myIdx !== -1) {
        const myScore = waitingMatch.players[myIdx].totalScore;
        const otherScores = waitingMatch.players.filter((_, idx) => idx !== myIdx).map(p => p.totalScore);
        const maxOther = Math.max(...otherScores, -999);
        recordOperationalMetric(`online_participation_complete_${myScore === maxOther ? 'draw' : myScore > maxOther ? 'win' : 'loss'}`);
        if (myScore !== maxOther) {
          const opponents = waitingMatch.players
            .filter((_, idx) => idx !== myIdx && _.name)
            .map(p => p.name)
            .join(', ') || 'Avversario';
          recordMatchOutcome(
            myScore > maxOther,
            'online',
            opponents,
            myScore,
            maxOther
          ).then((res) => {
            if (res?.leveledUp) {
              setShowLevelUpLevel(res.newLevel);
            }
          });
          // Coin reward for online match (PREMIUM_ENABLED)
          if (PREMIUM_ENABLED && user) {
            const myScore = waitingMatch.players[myIdx].totalScore;
            const won = myScore > maxOther;
            earnCoins(won ? COIN_REWARDS.WIN_MATCH : 5, won ? 'online_match_win' : 'online_match_loss');
          }
        }
      }
    }
  }, [waitingMatch, onlineMatchId, user, recordMatchOutcome, earnCoins]);

  // Fire 'tournament_win' achievement when a tournament ends (finished status)
  useEffect(() => {
    if (!user) return;
    const fbTournamentsRef = ref(db, `users/${user.uid}/tournament/privateTournaments`);
    const unsub = onValue(
      fbTournamentsRef,
      async (snapshot) => {
        const fb = snapshot.val() as Record<string, Omit<Tournament, 'id'>> | null;
        if (!fb) return;
        for (const [, data] of Object.entries(fb)) {
          if (data.status === 'finished' && data.players?.[user.uid]) {
            // Player was in this tournament when it ended — award tournament_win once
            const newlyUnlocked = await checkAndSave({ tournament_win: 1 });
            if (newlyUnlocked.length > 0) {
              console.log('[App] tournament_win achievement unlocked!');
            }
 break; // only fire once per session (checkAndSave guards against re-unlock)
          }
        }
      },
      () => {} // silently ignore errors
    );
    return () => unsub();
  }, [user, checkAndSave]);


  const roundStats = useMemo(() => {
    if (!gameState) return null;
    if (gameState.phase !== 'roundEnd' && gameState.phase !== 'gameOver') return null;
    if (gameState.roundResults) {
      const results = gameState.roundResults;
      return {
        pScore: results[0]?.score ?? 0,
        aiScore: results[1]?.score ?? 0,
        pResults: results[0] ?? null,
        aiResults: results[1] ?? null,
        results,
      };
    }
    const numPlayers = gameState.numPlayers;
    const scoreResults = calculateRoundScores(gameState.players, numPlayers);
    return {
      pScore: scoreResults.results[0]?.score ?? 0,
      aiScore: scoreResults.results[1]?.score ?? 0,
      pResults: scoreResults.results[0] ?? null,
      aiResults: scoreResults.results[1] ?? null,
      results: scoreResults.results,
    };
  }, [gameState?.phase, gameState?.roundResults, gameState?.players]);

  // Adapters: hook game logic + App-level UI side effects
  const startGame = useCallback((numPlayers: number, difficulty: 'easy' | 'medium' | 'hard') => {
    hasRecordedOfflineRef.current = false;
    recordOperationalMetric(`cpu_start_${difficulty}`);
    setCpuDifficulty(difficulty);
    gs.startGame(numPlayers);
    setGameMode('ai');
    setShowConfig(false);
    preloadAllCards();
  }, [gs]);

  const restartGame = useCallback(() => {
    hasRecordedOfflineRef.current = false;
    recordOperationalMetric(`cpu_start_${cpuDifficulty}`);
    gs.restartGame();
    preloadAllCards();
  }, [gs, gameState?.numPlayers, cpuDifficulty]);

  // Preload all 40 card images at component mount for instant rendering
  useEffect(() => {
    preloadAllCards();
  }, []);

  // Preload all visible card images for smooth animations
  useEffect(() => {
    if (!gameState) return;
    const preload = (card: Card) => {
      const suitName = SUIT_IMG_MAP[card.suit];
      const img = new Image();
      img.src = `/cards_optimized/${suitName}_${card.value}.webp`;
    };
    gameState.table.forEach(preload);
    gameState.players[0].hand.forEach(preload);
    gameState.players[1].hand.forEach(preload);
    if (gameState.lastPlayedCard) preload(gameState.lastPlayedCard.card);
  }, [gameState]);

  // Reset expanded card when AI plays
  useEffect(() => {
    if (gameState && gameState.currentPlayerIndex !== 0) {
      setHoveredCardId(null);
    }
  }, [gameState?.currentPlayerIndex]);

// AI Logic
useEffect(() => {
  const gs = gameStateRef.current;
  if (!gs || gs.currentPlayerIndex <= 0 || gs.phase !== 'playing' || gs.lastPlayedCard) return;

  const timer = setTimeout(async () => {
    const currentGameState = gameStateRef.current;
    if (!currentGameState || currentGameState.currentPlayerIndex <= 0 || currentGameState.phase !== 'playing') return;

    const currentIndex = currentGameState.currentPlayerIndex;

    const bestMove = await getBestMove({
      players: currentGameState.players,
      table: currentGameState.table,
      currentPlayerIndex: currentIndex,
      deck: currentGameState.deck,
      numPlayers: currentGameState.numPlayers,
      difficulty: cpuDifficulty,
    });

    if (bestMove) {
      if (!currentGameState.players[currentIndex].hasDeclaredLowHand) {
        const hand = currentGameState.players[currentIndex].hand;
        const sum = getHandSum(hand);
        if (hand.length === 3 && sum <= GAME_CONSTANTS.SBARAZZO_DECLARE_THRESHOLD) {
          handleLowHandDeclaration(currentIndex);
        }
      }
      if (bestMove.capture) {
        performCapture(currentIndex, bestMove.card, bestMove.capture);
      } else {
        // Check Three Aces before playing to table (non-capture move)
        if (!currentGameState.players[currentIndex].hasDeclaredThreeAces) {
          const hand = currentGameState.players[currentIndex].hand;
          const aces = hand.filter(c => c.value === 1);
          if (aces.length === 3) {
            handleThreeAcesDeclaration(currentIndex);
          }
        }
        setGameState(prev => {
          if (!prev) return null;
          const newPlayers = prev.players.map((p, i) => i === currentIndex ? {
            ...p,
            hand: p.hand.filter(c => c.id !== bestMove!.card.id)
          } : p);
          return {
            ...prev,
            players: newPlayers,
            lastPlayedCard: { card: bestMove!.card, playerIndex: currentIndex }
          };
        });
        setTimeout(() => {
          const playingIndex = currentIndex;
          setGameState(prev => {
            if (!prev) return null;
            const newPlayers = prev.players.map((p, i) => i === playingIndex ? {
              ...p,
              hand: p.hand.filter(c => c.id !== bestMove!.card.id),
              hasDeclaredLowHand: p.hasDeclaredLowHand,
              hasDeclaredThreeAces: p.hasDeclaredThreeAces,
            } : p);
            return {
              ...prev,
              table: [...prev.table, bestMove!.card],
              lastPlayedCard: null,
              players: newPlayers,
              currentPlayerIndex: (playingIndex + prev.numPlayers - 1) % prev.numPlayers,
            };
          });
        }, 800);
      }
    }
  }, GAME_CONSTANTS.CPU_DELAY_MS);
  return () => clearTimeout(timer);
}, [gameState?.currentPlayerIndex, gameState?.phase, gameState?.lastPlayedCard, cpuDifficulty]);

  // Handle hand dealing and round end
  useEffect(() => {
    if (!gameState) return;

    if (gameState.players.every(p => p.hand.length === 0)) {
      if (gameState.deck.length >= gameState.numPlayers * 3) {
        // Deal 3 cards each to all players
        const timer = setTimeout(() => {
          setGameState(prev => {
            if (!prev) return null;
            const newDeck = [...prev.deck];
            const newHands: Card[][] = [];
            for (let i = 0; i < prev.numPlayers; i++) {
              newHands.push(newDeck.splice(0, 3));
            }
            const newPlayers = prev.players.map((p, i) => ({
              ...p,
              hand: newHands[i],
              // Reset declaration flags to false on new deal so they can declare sbarazzo/aces again!
              hasDeclaredLowHand: false,
              hasDeclaredThreeAces: false,
              lowHandCount: p.lowHandCount ?? 0,
              threeAcesCount: p.threeAcesCount ?? 0,
            }));
            // If player 0 hasn't declared and has a low hand, give them the turn immediately
            // so the sbarazzo button appears without waiting for turn rotation
            const p0 = newPlayers[0];
            const p0Sum = getHandSum(p0.hand);
            const giveTurnToP0 = !p0.hasDeclaredLowHand && p0.hand.length === 3 && p0Sum <= GAME_CONSTANTS.SBARAZZO_DECLARE_THRESHOLD;
            return {
              ...prev,
              deck: newDeck,
              players: newPlayers,
              phase: 'playing',
              currentPlayerIndex: giveTurnToP0 ? 0 : prev.currentPlayerIndex,
              lastPlayedCard: giveTurnToP0 ? null : prev.lastPlayedCard,
            };
          });
          setDealId(d => d + 1);
          // Reset card reveal state on mid-deal
          setRevealedCardIds(new Set());
        }, 800);
        return () => clearTimeout(timer);
      } else if (gameState.phase !== 'roundEnd' && gameState.phase !== 'gameOver') {
        // Last to capture takes everything
        const timer = setTimeout(() => {
          setGameState(prev => {
            if (!prev) return null;
            const lastCapturer = prev.lastToCapture ?? 0;
            const finalCaptured = [...prev.players[lastCapturer].captured, ...prev.table];
            
            // Preserve hasDeclaredLowHand and hasDeclaredThreeAces from CURRENT prev (not from closure)
            // This fixes the race condition where sbarazzo was declared after the timeout was created
            const currentDeclaredLow = prev.players.map(p => p.hasDeclaredLowHand);
            const currentDeclaredThree = prev.players.map(p => p.hasDeclaredThreeAces);
            const currentLowHandCount = prev.players.map(p => p.lowHandCount ?? 0);
            const currentThreeAcesCount = prev.players.map(p => p.threeAcesCount ?? 0);
            
            const newPlayers = prev.players.map((p, i) => {
              if (i === lastCapturer) {
                return { ...p, captured: finalCaptured };
              }
              return p;
            }).map((p, i) => ({
              ...p,
              hasDeclaredLowHand: currentDeclaredLow[i] ?? false,
              hasDeclaredThreeAces: currentDeclaredThree[i] ?? false,
              lowHandCount: currentLowHandCount[i],
              threeAcesCount: currentThreeAcesCount[i],
            }));
            
            const results = calculateRoundScores(newPlayers, prev.numPlayers);
            console.log('[ROUNDEND]', JSON.stringify({
              lastCapturer: lastCapturer ?? 0,
              tableCards: prev.table.map(c => `${c.suit}-${c.value}`),
              players: newPlayers.map((p, i) => ({
                name: p.name,
                captured: p.captured.length,
                coins: p.captured.filter(c => c.suit === 'denari').length,
                scope: p.scope,
                lowHand: p.hasDeclaredLowHand,
                threeAces: p.hasDeclaredThreeAces,
                settebello: p.captured.some(c => c.suit === 'denari' && c.value === 7),
                dueDenari: p.captured.some(c => c.suit === 'denari' && c.value === 2),
                dieciDenari: p.captured.some(c => c.suit === 'denari' && c.value === 10),
                primiera: results.results[i]?.primiera ?? 0,
              })),
              roundScores: results.results.map(r => r.score),
              totalScores: results.totalScores,
              newTotalScores: results.totalScores.map((score, i) => {
                const total = newPlayers[i].totalScore + score;
                return total === -11 ? 0 : total;
              }),
            }));
            const newTotals = results.totalScores.map((score, i) => {
              const total = newPlayers[i].totalScore + score;
              return total === -11 ? 0 : total;
            });
            const updatedPlayers = newPlayers.map((p, i) => ({
              ...p,
              hasDeclaredLowHand: p.hasDeclaredLowHand,
              hasDeclaredThreeAces: p.hasDeclaredThreeAces,
              lowHandCount: p.lowHandCount ?? 0,
              threeAcesCount: p.threeAcesCount ?? 0,
              totalScore: newTotals[i],
              currentRoundScore: results.totalScores[i]
            }));

            const gameOverReached = newTotals.some(t => t >= GAME_CONSTANTS.TARGET_SCORE);
            if (gameOverReached) {
              return {
                ...prev,
                table: [],
                players: updatedPlayers,
                phase: 'gameOver',
                roundResults: results.results
              };
            }

            return {
              ...prev,
              table: [],
              players: updatedPlayers,
              phase: 'roundEnd',
              roundResults: results.results
            };
          });
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [gameState?.players.map(p => p.hand.length).join(','), gameState?.deck.length]);

  // Render the appropriate screen
  let screenContent = null;
  if (gameMode === 'menu') {
    screenContent = (
      <MainMenu
        profile={profile}
        profileLoading={profileLoading}
        onUpdateName={updateDisplayName}
        onUpdateCustomization={updateProfileCustomization}
        onQuickPlay={() => {
          trackEvent('game_start', { mode: 'cpu', difficulty: 'medium', players: 2, surface: 'play_menu' });
          startGame(2, 'medium');
        }}
        onPlayVsCPU={() => setGameMode('config')}
        onPlayOnline={() => setGameMode('online')}
        user={user}
        logout={logout}
        onOpenAuth={() => setShowAuthModal(true)}
        onManageCookies={() => setForceShowCookieConsent(true)}
        coinsBalance={PREMIUM_ENABLED ? coinsBalance : undefined}
        onOpenShop={PREMIUM_ENABLED ? () => setShowShopModal(true) : undefined}
        onOpenAchievements={() => setShowAchievementsModal(true)}
        onOpenFriends={() => setShowFriendsModal(true)}
        onOpenDailyChallenges={() => setShowDailyChallenges(true)}
        onOpenTournament={() => setShowTournament(true)}
      />
    );
  } else if (gameMode === 'config') {
    screenContent = (
      <ConfigScreen
        stats={profile?.stats || { played: 0, won: 0, lost: 0 }}
        onStartGame={startGame}
        onBack={() => setGameMode('menu')}
      />
    );
  } else if (gameMode === 'online') {
    if (onlineStatus === 'waiting' && onlineMatchId) {
      screenContent = (
        <WaitingRoom
          code={onlineMatchId}
          isHost={isHost}
          match={waitingMatch}
          myUid={user?.uid ?? ''}
          onCancel={handleOnlineBack}
        />
      );
    } else if (onlineStatus === 'playing' && onlineMatchId) {
      screenContent = (
        <OnlineGameView
          matchId={onlineMatchId}
          myUid={user?.uid ?? ''}
          onBack={handleOnlineBack}
        />
      );
    } else {
      screenContent = (
        <MenuOnline
          myUid={user?.uid ?? ''}
          initialPlayerName={profile?.displayName}
          onMatchCreated={handleMatchCreated}
          onMatchJoined={handleMatchJoined}
          onBack={handleOnlineBack}
        />
      );
    }
  } else if (gameState) {
    screenContent = (
      <GameBoard
        gameState={gameState}
        selectedCard={selectedCard}
        hoveredCardId={hoveredCardId}
        possibleCaptures={possibleCaptures}
        capturingCards={capturingCards}
        showRules={showRules}
        showCapturesMenu={showCapturesMenu}
        scopaParticleSeeds={scopaParticleSeeds}
        errorMessage={errorMessage}
        roundStats={roundStats}
        dealId={dealId}
        revealedCardIds={revealedCardIds}
        revealingCardIds={revealingCardIds}
        viewportWidth={viewportWidth}
        onCardSelect={playCard}
        onCaptureSelect={(cap) => selectedCard && performCapture(0, selectedCard, cap)}
        onHoverCard={setHoveredCardId}
        onToggleRules={() => setShowRules(r => !r)}
        onDismissCapturesMenu={() => setShowCapturesMenu(false)}
        onNextRound={handleNextRound}
        onRestart={restartGame}
        onExit={() => {
          decrementBoost();
          setGameMode('menu');
        }}
        onDeclareLowHand={() => handleLowHandDeclaration(0)}
        onDeclareThreeAces={() => handleThreeAcesDeclaration(0)}
        playerName={gameState.players[0].name}
      />
    );
  }

  return (
    <>
      <BackgroundParticles />
      {screenContent}
      {showLevelUpLevel !== null && (
        <LevelUpModal
          level={showLevelUpLevel}
          onClose={() => setShowLevelUpLevel(null)}
          isAnonymous={user?.isAnonymous ?? true}
          onOpenAuth={() => {
            setShowLevelUpLevel(null);
            setShowAuthModal(true);
          }}
          onClaimReward={(coins) => earnCoins(coins, `level_up_${showLevelUpLevel}`)}
        />
      )}
      <AnimatePresence>
        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            isAnonymous={user?.isAnonymous ?? true}
            loginWithGoogle={loginWithGoogle}
            linkWithGoogle={linkWithGoogle}
            registerAndLinkEmail={registerAndLinkEmail}
            loginWithEmail={loginWithEmail}
          />
        )}
        {showShopModal && (
          <Shop
            user={user}
            onClose={() => setShowShopModal(false)}
            equippedCardBack={profile?.equipped?.cardBack || 'cb_classic'}
            ownedCardBacks={profile?.owned?.cardBacks || ['cb_classic']}
            coinsBalance={coinsBalance}
            onEquip={(id) => {
              equipSkin(id);
            }}
            onPurchase={async (id, price) => {
              if (!user) return false;
              // Verify real Firebase balance (local may be stale if localOverride)
              const coinsRef = ref(db, `users/${user.uid}/coins/balance`);
              const { get, runTransaction } = await import('firebase/database');
              const snap = await get(coinsRef);
              const fbBalance = snap.val() ?? 0;
              if (fbBalance < price) return false;
              // Atomic spend via runTransaction
              const success = await new Promise<boolean>((resolve) => {
                runTransaction(coinsRef, (current) => {
                  const bal = current ?? 0;
                  if (bal < price) { resolve(false); return bal; }
                  resolve(true);
                  return bal - price;
                });
              });
              if (!success) return false;
              // Add skin to owned
              const owned = profile?.owned?.cardBacks || ['cb_classic'];
              if (!owned.includes(id)) {
                await updateOwnedSkins({ cardBacks: [...owned, id] });
              }
              return true;
            }}
          />
        )}
        {showAchievementsModal && (
          <AchievementsModal
            achievements={achievements}
            onClose={() => setShowAchievementsModal(false)}
          />
        )}
        {showFriendsModal && user && (
          <FriendsModal
            uid={user.uid}
            onClose={() => setShowFriendsModal(false)}
            onChallenge={async (friendUid, friendName) => {
              setShowFriendsModal(false);
              if (!user) return;
              try {
                const challengeId = await challengeFriend(user.uid, profile?.displayName || 'Player', friendUid);
                console.log('[App] Challenge sent to', friendName, ':', challengeId);
              } catch (err) {
                console.error('[App] challengeFriend failed:', err);
              }
            }}
          />
        )}
        {pendingChallenge && user && (
          <IncomingChallengeModal
            challenge={pendingChallenge}
            myUid={user.uid}
            myName={profile?.displayName || 'Player'}
            onAccept={(matchCode) => {
              setPendingChallenge(null);
              setOnlineMatchId(matchCode);
              setOnlineStatus('playing');
              setGameMode('online');
            }}
            onDecline={async () => {
              if (pendingChallenge) {
                await removeChallenge(user.uid, pendingChallenge.challengeId);
              }
              setPendingChallenge(null);
            }}
          />
        )}
        {showDailyChallenges && user && (
          <DailyChallenges
            isOpen={showDailyChallenges}
            onClose={() => setShowDailyChallenges(false)}
            uid={user.uid}
          />
        )}
        {showTournament && (
          <PrivateTournamentModal
            user={user}
            onClose={() => setShowTournament(false)}
          />
        )}
      </AnimatePresence>
      <CookieConsent 
        forceShow={forceShowCookieConsent} 
        onCloseForceShow={() => setForceShowCookieConsent(false)} 
      />
            {/* TODO: riattivare VideoAdModal in fase successiva */}
      {/* <VideoAdModal showOnMount={showVideoAdModal} /> */}
      <Footer />
    </>
  );
}
